import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { MessageCircle, Send, Upload, Bot, Store } from "lucide-react";

const stateLabel = (s) => ({ accepted: "inviato", sent: "inviato", duplicate: "già inviato", skipped: "non inviato", failed: "errore" }[s] || s || "—");
const REASONS = { disabled: "WhatsApp disattivato", not_verified: "WhatsApp non verificato", no_automation_for_event: "automazione weekly_summary_branch non collegata in Spoki", phone_missing: "telefono mancante", not_in_whitelist: "numero non in whitelist test", spoki_not_bound: "Spoki non inizializzato" };
const reasonLabel = (r) => (r ? ` (${REASONS[r] || r})` : "");
const stateCls = (s) => (s === "accepted" || s === "sent" ? "text-emerald-400" : s === "failed" ? "text-red-400" : "text-amber-300");
const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export function BranchReportRow({ branch, crmReady, onDone }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const last = branch.weekly_report_last;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/branches/${branch.id}/weekly-report/run-now`);
      const r = data.sent?.[0];
      if (r) { toast.success(`Report ${branch.name}: WhatsApp ${stateLabel(r.whatsapp?.state)}${reasonLabel(r.whatsapp?.reason)}`); setPreview(r); }
      else if (data.errors?.length) toast.error(data.errors[0].error);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2.5 space-y-1.5" data-testid={`branch-report-${branch.id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="min-w-0 flex items-center gap-1.5 text-zinc-300"><MessageCircle className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
          <span className="truncate" data-testid={`branch-manager-${branch.id}`}>{branch.manager_phone ? `${branch.manager_name ? branch.manager_name + " · " : ""}${branch.manager_phone}` : "Nessun responsabile WhatsApp"}</span>
        </div>
        {crmReady && branch.manager_phone && (
          <Button size="sm" variant="outline" disabled={busy} onClick={send} data-testid={`branch-report-send-${branch.id}`} className="h-7 text-[11px] border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
            <Send className="h-3 w-3 mr-1" /> {busy ? "Invio…" : "Report ora"}
          </Button>
        )}
      </div>
      {last && <div className="text-[11px] text-zinc-500" data-testid={`branch-report-last-${branch.id}`}>Ultimo report: settimana dal {last.week?.split("-").reverse().join("/")} · {fmtAt(last.at)} · <span className={stateCls(last.state)}>{stateLabel(last.state)}</span>{reasonLabel(last.reason)}</div>}
      {preview && (
        <div className="text-[11px] text-zinc-400 whitespace-pre-line border-t border-zinc-800 pt-1.5" data-testid={`branch-report-preview-${branch.id}`}>
          {preview.summary}{preview.ai_insights ? `\n\n🤖 ${preview.ai_insights}` : ""}
        </div>
      )}
    </div>
  );
}

export function BranchReportsToolbar({ status, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const running = status?.run?.state === "running";
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => onDone?.(), 5000);
    return () => clearInterval(t);
  }, [running, onDone]);
  if (!status?.crm_ready) return null;
  const syncStores = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/branches/sync-from-crm"); toast.success(`${data.created.length} sedi create dai punti vendita CRM`); onDone?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSyncing(false); }
  };
  const doImport = async () => {
    setBusy(true);
    try { const { data } = await api.post("/branches/managers/import", { text }); setResult(data); toast.success(`${data.updated.length} sedi aggiornate`); onDone?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const sendAll = async () => {
    if (!window.confirm(`Inviare ora il report della settimana ${status.week_from} → ${status.week_to} a ${status.managers} responsabili?`)) return;
    setSendingAll(true);
    try { const { data } = await api.post("/branches/weekly-report/run-now"); toast.success(`Invio avviato per ${data.total} sedi: circa ${Math.max(1, Math.round(data.total * 0.5))} min. Puoi continuare a lavorare.`); onDone?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSendingAll(false); }
  };
  const run = status.run;
  return (
    <div className="card-elev p-4 flex flex-wrap items-center gap-3" data-testid="branch-reports-toolbar">
      <div className="flex-1 min-w-[240px]">
        <div className="font-heading font-bold text-sm flex items-center gap-2"><Bot className="h-4 w-4 text-[color:var(--paprika)]" /> Report AI settimanale per sede</div>
        <p className="text-[11px] text-zinc-500 mt-0.5">Ogni settimana (dal lunedì mattina, appena il CRM ha ricevuto gli scontrini di domenica) i responsabili con numero WhatsApp ricevono scontrini, incasso e consigli AI della propria sede. Responsabili impostati: <b data-testid="branch-reports-managers">{status.managers}</b>. Collega l'automazione <code>weekly_summary_branch</code> in Spoki.</p>
        {run && (
          <p className="text-[11px] mt-1" data-testid="branch-reports-run">
            {running ? <span className="text-amber-300">Invio in corso a {run.total} sedi… (avviato {fmtAt(run.started_at)})</span>
              : run.error ? <span className="text-red-400">Ultimo invio fallito: {run.error}</span>
              : run.result ? <span className="text-zinc-400">Ultimo invio a tutte ({fmtAt(run.finished_at)}): {run.result.sent} report generati, {run.result.whatsapp_ok} WhatsApp inviati, {run.result.errors} errori</span> : null}
          </p>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={() => { setResult(null); setOpen(true); }} data-testid="branch-managers-import-open" className="border-zinc-800"><Upload className="h-3.5 w-3.5 mr-1" /> Importa responsabili</Button>
      {status.crm_stores_missing > 0 && (
        <Button size="sm" variant="outline" disabled={syncing} onClick={syncStores} data-testid="branch-sync-crm" className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10"><Store className="h-3.5 w-3.5 mr-1" /> {syncing ? "Creo…" : `Crea ${status.crm_stores_missing} sedi dal CRM`}</Button>
      )}
      <Button size="sm" disabled={sendingAll || running || !status.managers} onClick={sendAll} data-testid="branch-reports-send-all" className="bg-emerald-600 hover:bg-emerald-500"><Send className="h-3.5 w-3.5 mr-1" /> {running ? "Invio in corso…" : sendingAll ? "Avvio…" : "Invia report a tutte"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800" data-testid="branch-managers-import-dialog">
          <DialogHeader><DialogTitle>Importa responsabili punti vendita</DialogTitle></DialogHeader>
          <p className="text-[11px] text-zinc-500">Una riga per sede: <code>nome sede;telefono WhatsApp;nome responsabile</code> (il nome responsabile è opzionale). La sede si abbina per nome o per punto vendita CRM.</p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={"Eté Battipaglia;+39 333 1234567;Mario Rossi\nEté Salerno;+39 334 7654321"} className="w-full bg-zinc-950 border border-zinc-800 rounded-md p-2 text-sm font-mono" data-testid="branch-managers-import-text" />
          {result && (
            <div className="text-[11px] space-y-1" data-testid="branch-managers-import-result">
              <div className="text-emerald-400">Aggiornate: {result.updated.length}{result.updated.length ? " — " + result.updated.map((u) => u.name).join(", ") : ""}</div>
              {result.not_found.length > 0 && <div className="text-amber-300">Sedi non trovate: {result.not_found.join(", ")}</div>}
              {result.invalid.length > 0 && <div className="text-red-400">Righe non valide: {result.invalid.length}</div>}
            </div>
          )}
          <Button disabled={busy || text.trim().length < 3} onClick={doImport} data-testid="branch-managers-import-submit" className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">{busy ? "Importo…" : "Importa"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
